import { useLocaleStore } from "@/store/locale.store";

// English is the source of truth for the shape; `Translations` is derived from it.
// We intentionally do NOT use `as const` so values widen to `string` — that keeps
// the `en | fr` union assignable to `Translations` when `t` is passed around.
const translations = {
  en: {
    // Sidebar nav
    nav: {
      home: "Home",
      grocery: "Grocery",
      browseAll: "Browse All",
      gifts: "Gifts",
      orders: "Orders",
      account: "Account",
    },
    // Account popup
    accountPopup: {
      title: "Account",
      myAccount: "My account",
      addresses: "Addresses",
      savedStores: "Saved Stores",
      enterprise: "Fast for Businesses",
      accountSettings: "Account Settings",
      wallet: "Wallet",
      subscription: "Subscription",
      about: "About",
      helpSupport: "Help & support",
      language: "Language",
      signOut: "Sign Out",
      signingOut: "Signing out…",
    },
    // Reusable words
    common: {
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      back: "Back",
      seeAll: "See all",
      seeMore: "See more",
      seeLess: "See less",
      noResults: "No results",
      delete: "Delete",
      edit: "Edit",
      confirm: "Confirm",
      retry: "Retry",
      loading: "Loading…",
      search: "Search",
      clear: "Clear",
      add: "Add",
      remove: "Remove",
      previous: "Previous",
      next: "Next",
      today: "Today",
      tomorrow: "Tomorrow",
      dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    },
    // Sidebar (auth + logout)
    sidebar: {
      signIn: "Sign in",
      signOut: "Sign out",
      signingOut: "Signing out…",
      logoutTitle: "Sign out",
      logoutConfirm: "Do you really want to sign out of your account?",
    },
    // Header (search + address picker)
    header: {
      searchRestaurant: "Search a restaurant…",
      searchProduct: "Search a product…",
      searchPartner: "Search a partner",
      recentSearches: "Recent searches",
      setAddress: "Set an address",
      myCurrentAddress: "My current address",
      favorites: "Favorites",
      enterAddress: "Enter your address",
      searchAddress: "Search an address…",
      useCurrentLocation: "Use my current location",
      addAddress: "Add an address",
      addAddressSub: "Search and save a new address",
      newAddress: "New address",
      adjust: "Adjust",
      apartmentOrSuite: "Apartment or suite",
      addressName: "Address name",
      landmark: "Landmark",
      addressType: "Address type",
      typeHome: "Home",
      typeWork: "Work",
      typeOther: "Other",
      adjustPin: "Adjust the pin",
      currentLocation: "Current location",
      favoriteAddresses: "Favorite addresses",
      noFavoriteAddress: "No favorite address",
      addToFavorites: "Add to favorites",
      addressChanged: (name: string) => `Address changed: ${name}`,
    },
    // Footer
    footer: {
      aboutUs: "About us",
      about: "About",
      careers: "Careers",
      blog: "Blog",
      press: "Press",
      help: "Help",
      assistant: "Fast Assistant",
      deliveryHelp: "How delivery works",
      brands: "Brands",
      accountDetails: "Account details",
      orderHistory: "Order history",
      support: "Support",
      partners: "Partners",
      becomePartner: "Become a partner",
      becomeCourier: "Become a courier",
      legalNotice: "Legal notice",
      terms: "Terms of use",
      termsPro: "Pro terms of use",
      privacy: "Privacy",
      byCity: "Delivery by city",
      allCities: "All cities",
      copyright: "© 2026 SARL HELLO DELIVERY – All rights reserved.",
    },
    // Order tracking map
    riderMap: {
      locatingCourier: "Locating the courier…",
      mapWillAppear: "The map will appear as soon as the courier shares their location.",
      courierOnTheWay: "Courier on the way",
      deliveryLocation: "Delivery location",
    },
    // Home / store listing
    home: {
      allPartners: "All partners",
      deliveryFee: "Delivery fee",
      feeVaries: "Fees vary by store and your address.",
      seeResults: "See results",
      reset: "Reset",
      resetFilters: "Reset filters",
      noResultsTitle: "No results",
      noResultsSub: "Your filters don't match any store in your area.",
      banner: "Banner",
      bannerCta: "Learn more",
      loadError: "Failed to load stores. Please try again later.",
      deals: "Deals",
      over4: "Over 4.0 ★",
      under30: "Under 30 min",
      allFees: "All fees",
      feeUnder: (n: number) => `Delivery ≤ ${n} DA`,
      lessThan: (n: number) => `Under ${n} DA`,
      results: (n: number) => `${n} result${n !== 1 ? "s" : ""}`,
    },
    // Store card badges + item/menu
    store: {
      preorder: "Pre-order",
      closed: "Closed",
      sponsored: "Sponsored",
      viewStore: "View store",
      selectOne: "Select 1 option",
      selectOptions: (required: boolean, max: number) =>
        `Select ${required ? "at least 1" : "up to " + max} option(s)`,
      required: "Required",
      optional: "Optional",
      chooseOption: "Choose an option",
      addToCart: "Add to cart",
    },
    // Payment return (CIB / SATIM)
    payment: {
      preparingTitle: "Preparing your payment",
      preparingSubtitle: "You're being redirected to the secure payment page…",
      verifyingTitle: "Confirming your payment",
      verifyingSubtitle: "Please wait while we confirm your card payment…",
      successTitle: "Payment successful",
      successSubtitle: "Your order is confirmed and being prepared.",
      topupSuccessSubtitle: "Your wallet has been topped up.",
      failedTitle: "Payment failed",
      failedSubtitle: "Your payment could not be completed. You haven't been charged. Please try again.",
      pendingTitle: "Payment is being verified",
      pendingSubtitle: "This is taking longer than expected. Please keep this page open.",
      trackOrder: "Track my order",
      viewOrders: "View my orders",
      done: "Done",
    },
    // Cart / checkout
    cart: {
      title: "Cart",
      products: "Products",
      add: "Add",
      cash: "Cash",
      cashOnDelivery: "Cash on delivery",
      atDelivery: "On delivery",
      cibCard: "CIB card",
      cardPayment: "Card payment",
      fastWallet: "Wallet",
      payWithBalance: "Pay with your balance",
      businessWallet: "Business wallet",
      corporate: "Corporate",
      paymentFooter: (m: string) => `Payment: ${m}`,
      paymentCorporate: "Payment: Business wallet",
      subtotal: "Subtotal",
      deliveryFee: "Delivery fee",
      serviceFee: "Service fee",
      taxes: "Taxes",
      storeTaxes: "Store taxes",
      discount: "Discount",
      tip: "Tip",
      total: "Total",
      deliveryFeeInfo: "These fees are calculated based on your distance from the partner, as well as various other factors such as weather, traffic and current demand on the platform.",
      serviceFeeInfo: "These fees help cover the costs of maintaining and continuously improving our app, ensuring a smooth and reliable experience on the platform.",
      deliveryAddress: "Delivery address",
      noAddressSelected: "No address selected",
      storeNotInZone: "This store doesn't deliver to this address.",
      orderPlaced: "Order placed!",
      orderConfirmed: "Order confirmed",
      orderSuccess: "Your order was placed successfully.",
      trackMyOrder: "Track my order",
      addComment: "Add a comment",
      commentPlaceholder: "E.g. no chili, well done…",
      deliveryInstructionsPlaceholder: "Delivery instructions (floor, code…)",
      sendToSomeoneElse: "Send to someone else",
      recipientHint: "Add their details to help the courier",
      recipientNamePlaceholder: "Recipient name",
      phoneNumberPlaceholder: "Phone number",
      recipientConsent: "By adding the recipient's details, you confirm you have their consent to share their information with Fast and the courier, solely for the delivery of this order.",
      deliveryOptions: "Delivery options",
      priority: "Priority",
      soon: "Soon",
      standard: "Standard",
      schedule: "Schedule",
      chooseTime: "Choose a time",
      codeApplied: (c: string) => `Code applied: ${c}`,
      addPromoCode: "Add a promo code",
      discountOff: (n: string) => `-${n} DA off`,
      tipCourier: "Help your rider",
      tipNote: "100% of the tip goes to your rider. Fast takes no commission.",
      tipRoundUp: "Round up the total",
      tipRoundUpTotal: "Your total becomes",
      tipRoundUpAlready: "Total already rounded",
      tipSaveFuture: "Always round up my next orders",
      tipSaveFutureActive: "Automatic round-up is on",
      tipSaveFutureEdit: "Change",
      tipOrAdd: "Or add a tip",
      none: "None",
      paymentMethod: "Payment method",
      personal: "Personal",
      personalOrders: "Personal orders",
      proOrders: "Pro orders",
      summary: "Summary",
      genericError: "An error occurred. Please try again.",
      confirming: "Confirming…",
      order: "Order",
      noPaymentMethod: "No payment method available.",
      scheduleDelivery: "Schedule delivery",
      openLabel: (a: string, b: string) => `Open: ${a} – ${b}`,
      receiveBetween: "You want to receive your order between:",
      noSlots: "No slot available for this day.",
      promoCode: "Promo code",
      enterPromoCode: "Enter a promo code",
      applying: "Applying…",
      apply: "Apply",
      offersRewards: "Offers & rewards",
      use: "Use",
      noPublicOffers: "No public offer available.",
      promoApplied: "Promo code applied.",
      promoNotFound: "Unable to find the requested campaign",
      promoValidateError: "Unable to validate this promo code.",
    },
    // Landing / homepage (guests)
    landing: {
      login: "Log in",
      signup: "Sign up",
      goToApp: "Order now",
      continueToApp: "Continue to my orders",
      heroTitleLine1: "ORDER FROM THE BEST SHOPS",
      heroTitleLine2: "NEAR YOU",
      addressPlaceholder: "Enter your delivery address",
      continue: "Continue",
      loginForSavedAddress: "Log in to use a saved address",
      browseWithoutAccount: "Browse without an account",
      fastDeliveryTitle: "Fast Delivery",
      fastDeliveryDesc: "Get your order in just a few minutes.",
      localStoresTitle: "Local Shops",
      localStoresDesc: "Discover the best restaurants and shops near you.",
      easyOrderTitle: "Easy Ordering",
      easyOrderDesc: "Order from your phone in just a few taps.",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      grocery: "Épicerie",
      browseAll: "Tout parcourir",
      gifts: "Offres",
      orders: "Commandes",
      account: "Compte",
    },
    accountPopup: {
      title: "Compte",
      myAccount: "Mon compte",
      addresses: "Adresses",
      savedStores: "Commerces enregistrés",
      enterprise: "Fast pour les Entreprises",
      accountSettings: "Paramètres du compte",
      wallet: "Portefeuille",
      subscription: "Abonnement",
      about: "À propos",
      helpSupport: "Aide & support",
      language: "Langue",
      signOut: "Se déconnecter",
      signingOut: "Déconnexion…",
    },
    common: {
      cancel: "Annuler",
      save: "Enregistrer",
      saving: "Enregistrement…",
      back: "Retour",
      seeAll: "Voir tout",
      seeMore: "Voir plus",
      seeLess: "Voir moins",
      noResults: "Aucun résultat",
      delete: "Supprimer",
      edit: "Modifier",
      confirm: "Confirmer",
      retry: "Réessayer",
      loading: "Chargement…",
      search: "Rechercher",
      clear: "Effacer",
      add: "Ajouter",
      remove: "Retirer",
      previous: "Précédent",
      next: "Suivant",
      today: "Aujourd'hui",
      tomorrow: "Demain",
      dayNames: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
    },
    sidebar: {
      signIn: "Se connecter",
      signOut: "Se déconnecter",
      signingOut: "Déconnexion…",
      logoutTitle: "Se déconnecter",
      logoutConfirm: "Voulez-vous vraiment vous déconnecter de votre compte ?",
    },
    header: {
      searchRestaurant: "Rechercher un restaurant…",
      searchProduct: "Rechercher un produit…",
      searchPartner: "Rechercher un partenaire",
      recentSearches: "Recherches récentes",
      setAddress: "Définir une adresse",
      myCurrentAddress: "Mon adresse actuelle",
      favorites: "Favoris",
      enterAddress: "Entrez votre adresse",
      searchAddress: "Rechercher une adresse…",
      useCurrentLocation: "Utiliser ma position actuelle",
      addAddress: "Ajouter une adresse",
      addAddressSub: "Recherchez et enregistrez une nouvelle adresse",
      newAddress: "Nouvelle adresse",
      adjust: "Ajuster",
      apartmentOrSuite: "Appartement ou suite",
      addressName: "Nom de l'adresse",
      landmark: "Point de repère",
      addressType: "Type d'adresse",
      typeHome: "Domicile",
      typeWork: "Bureau",
      typeOther: "Autre",
      adjustPin: "Ajuster le repère",
      currentLocation: "Position actuelle",
      favoriteAddresses: "Adresses favorites",
      noFavoriteAddress: "Aucune adresse favorite",
      addToFavorites: "Ajouter aux favoris",
      addressChanged: (name: string) => `Adresse changée : ${name}`,
    },
    footer: {
      aboutUs: "À propos de nous",
      about: "À propos",
      careers: "Carrières",
      blog: "Blog",
      press: "Presse",
      help: "Aide",
      assistant: "L'assistant Fast",
      deliveryHelp: "Comment ça marche",
      brands: "Enseignes",
      accountDetails: "Détails du compte",
      orderHistory: "Historique des commandes",
      support: "Assistance",
      partners: "Partenaires",
      becomePartner: "Devenir partenaire",
      becomeCourier: "Devenir livreur",
      legalNotice: "Mentions légales",
      terms: "CGU",
      termsPro: "CGPU",
      privacy: "Confidentialité",
      byCity: "Livraison par ville",
      allCities: "Toutes les villes",
      copyright: "© 2026 SARL HELLO DELIVERY – Tous droits réservés.",
    },
    riderMap: {
      locatingCourier: "Localisation du livreur en cours…",
      mapWillAppear: "La carte s'affichera dès que le livreur partage sa position.",
      courierOnTheWay: "Livreur en route",
      deliveryLocation: "Lieu de livraison",
    },
    home: {
      allPartners: "Tous les partenaires",
      deliveryFee: "Frais de livraison",
      feeVaries: "Les frais varient selon le magasin et votre adresse.",
      seeResults: "Voir les résultats",
      reset: "Réinitialiser",
      resetFilters: "Réinitialiser les filtres",
      noResultsTitle: "Aucun résultat",
      noResultsSub: "Vos filtres ne correspondent à aucun magasin dans votre zone.",
      banner: "Bannière",
      bannerCta: "En savoir plus",
      loadError: "Échec du chargement des magasins. Veuillez réessayer plus tard.",
      deals: "Offres",
      over4: "Plus de 4.0 ★",
      under30: "Moins de 30 min",
      allFees: "Tous les frais",
      feeUnder: (n: number) => `Livraison ≤ ${n} DA`,
      lessThan: (n: number) => `Moins de ${n} DA`,
      results: (n: number) => `${n} résultat${n !== 1 ? "s" : ""}`,
    },
    store: {
      preorder: "Précommande",
      closed: "Fermé",
      sponsored: "Sponsorisé",
      viewStore: "Voir le magasin",
      selectOne: "Sélectionner 1 option",
      selectOptions: (required: boolean, max: number) =>
        `Sélectionner ${required ? "au moins 1" : "jusqu'à " + max} option(s)`,
      required: "Obligatoire",
      optional: "Optionnel",
      chooseOption: "Choisissez une option",
      addToCart: "Ajouter au panier",
    },
    payment: {
      preparingTitle: "Préparation du paiement",
      preparingSubtitle: "Vous allez être redirigé vers la page de paiement sécurisée…",
      verifyingTitle: "Confirmation de votre paiement",
      verifyingSubtitle: "Veuillez patienter pendant la confirmation de votre paiement par carte…",
      successTitle: "Paiement réussi",
      successSubtitle: "Votre commande est confirmée et en cours de préparation.",
      topupSuccessSubtitle: "Votre portefeuille a été rechargé.",
      failedTitle: "Paiement échoué",
      failedSubtitle: "Votre paiement n'a pas pu être finalisé. Vous n'avez pas été débité. Veuillez réessayer.",
      pendingTitle: "Paiement en cours de vérification",
      pendingSubtitle: "Cela prend plus de temps que prévu. Veuillez garder cette page ouverte.",
      trackOrder: "Suivre ma commande",
      viewOrders: "Voir mes commandes",
      done: "Terminé",
    },
    cart: {
      title: "Panier",
      products: "Produits",
      add: "Ajouter",
      cash: "Espèces",
      cashOnDelivery: "Espèces à la livraison",
      atDelivery: "À la livraison",
      cibCard: "Carte CIB",
      cardPayment: "Paiement par carte",
      fastWallet: "Portefeuille",
      payWithBalance: "Payer avec votre solde",
      businessWallet: "Wallet entreprise",
      corporate: "Corporate",
      paymentFooter: (m: string) => `Paiement : ${m}`,
      paymentCorporate: "Paiement : Wallet entreprise",
      subtotal: "Sous-total",
      deliveryFee: "Frais de livraison",
      serviceFee: "Frais de service",
      taxes: "Taxes",
      storeTaxes: "Taxes boutique",
      discount: "Réduction",
      tip: "Pourboire",
      total: "Total",
      deliveryFeeInfo: "Ces frais sont calculés en fonction de votre distance par rapport au partenaire, ainsi que de divers autres paramètres comme les conditions météorologiques, le trafic et la demande actuelle sur la plateforme.",
      serviceFeeInfo: "Ces frais permettent de couvrir les coûts associés à la maintenance et à l'amélioration continue de notre application, assurant une expérience fluide et fiable sur la plateforme.",
      deliveryAddress: "Adresse de livraison",
      noAddressSelected: "Aucune adresse sélectionnée",
      storeNotInZone: "Ce restaurant ne livre pas à cette adresse.",
      orderPlaced: "Commande passée !",
      orderConfirmed: "Commande confirmée",
      orderSuccess: "Votre commande a été passée avec succès.",
      trackMyOrder: "Suivre ma commande",
      addComment: "Ajouter un commentaire",
      commentPlaceholder: "Ex: sans piment, bien cuit…",
      deliveryInstructionsPlaceholder: "Instructions de livraison (étage, code…)",
      sendToSomeoneElse: "Envoyer à quelqu'un d'autre",
      recipientHint: "Ajoutez ses coordonnées pour aider le livreur",
      recipientNamePlaceholder: "Nom du destinataire",
      phoneNumberPlaceholder: "Numéro de téléphone",
      recipientConsent: "En ajoutant les coordonnées du destinataire, vous confirmez avoir son consentement pour partager ses informations avec Fast et le livreur, uniquement pour la livraison de cette commande.",
      deliveryOptions: "Options de livraison",
      priority: "Priorité",
      soon: "Bientôt",
      standard: "Standard",
      schedule: "Planifier",
      chooseTime: "Choisir l'heure",
      codeApplied: (c: string) => `Code appliqué : ${c}`,
      addPromoCode: "Ajouter un code promo",
      discountOff: (n: string) => `-${n} DA de réduction`,
      tipCourier: "Aidez le livreur",
      tipNote: "100 % du pourboire va au livreur. Fast ne prend aucune commission.",
      tipRoundUp: "Arrondir le total",
      tipRoundUpTotal: "Votre total passe à",
      tipRoundUpAlready: "Total déjà arrondi",
      tipSaveFuture: "Toujours arrondir mes prochaines commandes",
      tipSaveFutureActive: "Arrondi automatique activé",
      tipSaveFutureEdit: "Modifier",
      tipOrAdd: "Ou ajoutez un pourboire",
      none: "Aucun",
      paymentMethod: "Méthode de paiement",
      personal: "Personnel",
      personalOrders: "Commandes perso",
      proOrders: "Commandes pro",
      summary: "Récapitulatif",
      genericError: "Une erreur est survenue. Veuillez réessayer.",
      confirming: "Confirmation…",
      order: "Commander",
      noPaymentMethod: "Aucune méthode de paiement disponible.",
      scheduleDelivery: "Planifier la livraison",
      openLabel: (a: string, b: string) => `Ouvert : ${a} – ${b}`,
      receiveBetween: "Vous souhaitez recevoir votre commande entre :",
      noSlots: "Aucun créneau disponible pour ce jour.",
      promoCode: "Code promo",
      enterPromoCode: "Entrer un code promo",
      applying: "Application…",
      apply: "Appliquer",
      offersRewards: "Offres & récompenses",
      use: "Utiliser",
      noPublicOffers: "Aucune offre publique disponible.",
      promoApplied: "Code promo appliqué.",
      promoNotFound: "Impossible de trouver la campagne demandée",
      promoValidateError: "Impossible de valider ce code promo.",
    },
    landing: {
      login: "Connexion",
      goToApp: "Commander",
      continueToApp: "Continuer vers mes commandes",
      signup: "Inscription",
      heroTitleLine1: "COMMANDEZ CHEZ LES MEILLEURS BOUTIQUES",
      heroTitleLine2: "PRÈS DE CHEZ VOUS",
      addressPlaceholder: "Entrez votre adresse de livraison",
      continue: "Continuer",
      loginForSavedAddress: "Se connecter pour une adresse enregistrée",
      browseWithoutAccount: "Parcourir sans compte",
      fastDeliveryTitle: "Livraison Rapide",
      fastDeliveryDesc: "Recevez votre commande en quelques minutes.",
      localStoresTitle: "Commerces Locaux",
      localStoresDesc: "Découvrez les meilleurs restaurants et boutiques près de chez vous.",
      easyOrderTitle: "Commande Facile",
      easyOrderDesc: "Commandez depuis votre téléphone en quelques clics.",
    },
  },
};

export type Translations = typeof translations.en;

export function useTranslation(): Translations {
  const locale = useLocaleStore((s) => s.locale);
  return translations[locale];
}
